import React, { useEffect, useRef, useState } from 'react';

import Tooltip from '@material-ui/core/Tooltip';

import { TranslationLanguageSelectorDialog } from './translation-language-selector-dialog';
import {
  setStreamingHackDoneAction,
  updateIsTranslatingAction,
  updateMessageForLanguageAction,
  updateStreamingHackTextAction,
} from '../context/message-composer-actions';

import { UiButton } from '../../../ui/ui-button';
import { SplitButtonRenderProps, UiSplitButton } from '../../../ui/ui-split-button';
import {
  useMessageComposerDispatch,
  useMessageInputState,
} from '../context/message-composer-context';

import { useUnsentMessageTranslation } from '../../../messages/utils/use-unsent-message-translation';
import { LANGUAGES } from '../../../util/languages';
import { getCachedSelectedTranslationLanguage } from '../util/translation-language-selection';

type TranslateButtonProps = {
  /**
   * Resets the state of the translate button after a message sends
   */
  isMessageSending?: boolean;
  subject?: string;
  /**
   * Propagates the subject translation to the parent component. Also returns
   * the original subject when the translation is reverted
   */
  onSubjectChange?: (subject: string) => void;
  disabled: boolean;
  disabledTooltip: string | null;
};

/**
 * Without a language selection, should just be a plain button reading "Translate"
 * With a language selection, should be part of a button group and read "Translate into <language>"
 */
const TranslateActionButton = ({
  openClass,
  disabled: splitButtonDisabled,
  shouldRenderTranslateText,
  shouldRenderSplitButton,
  handleClick,
  loading,
  isLanguageSpecificButton,
  languageName,
}: {
  shouldRenderTranslateText: boolean;
  shouldRenderSplitButton: boolean;
  handleClick: () => void;
  isLanguageSpecificButton: boolean;
  loading?: boolean;
  languageName?: string;
} & SplitButtonRenderProps) => (
  <UiButton
    className={`u-width-100 ${
      shouldRenderSplitButton ? `u-border-right-none u-borderRadius-right-none ${openClass}` : ''
    }`}
    variant="tertiary"
    size="xxs"
    onClick={handleClick}
    loading={loading}
    disabled={splitButtonDisabled}
    dataCy="toolbar-translate"
  >
    {shouldRenderSplitButton || isLanguageSpecificButton
      ? shouldRenderTranslateText
        ? `Translate into ${languageName}`
        : 'Revert Translation'
      : 'Translate'}
  </UiButton>
);

const ChangeLanguageButton = ({ handleOpenDialogClick }: { handleOpenDialogClick: () => void }) => (
  <UiButton
    className="u-borderRadius-top-none u-textTransform-uppercase u-border-top-none u-width-100"
    variant="tertiary"
    size="xxs"
    onClick={handleOpenDialogClick}
    dataCy="toolbar-translate-change-language"
  >
    Change language
  </UiButton>
);

export const TranslateButton = ({
  isMessageSending,
  subject,
  onSubjectChange,
  disabled,
  disabledTooltip,
}: TranslateButtonProps) => {
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);

  const { message: messageBody, languageCode: messageInputLanguage } = useMessageInputState();
  const dispatch = useMessageComposerDispatch();

  /**
   * This button is used for the MLMC and appears for every MessageInput
   * box for each language. English is the default box with code 'en' and
   * can be used to send messages out in any language because it is always
   * visible. Any additional language is added specifically too send a message
   * in that language so we restrict the translate button to just translate
   * into the language of that box.
   *
   * This will only happen for bulk messaging
   */
  const isLanguageSpecificButton = messageInputLanguage !== 'en';

  const languageSelection = isLanguageSpecificButton
    ? messageInputLanguage
    : getCachedSelectedTranslationLanguage();
  const languageName = languageSelection ? LANGUAGES[languageSelection] : undefined;

  const shouldRenderSplitButton = Boolean(
    languageSelection && languageName && !isLanguageSpecificButton,
  );

  const originalSubject = useRef<string | undefined>(subject);
  const originalMessageBody = useRef<string>(messageBody);

  const { shouldShowTranslation, hideTranslation, loading, onTranslateMessageClick, reset } =
    useUnsentMessageTranslation({
      onMessageBodyChange: (newMessage) =>
        dispatch(
          updateStreamingHackTextAction({
            languageCode: messageInputLanguage,
            streamingHackText: newMessage,
          }),
        ),
      onFinished: () => {
        dispatch(setStreamingHackDoneAction({ languageCode: messageInputLanguage }));
      },
      onSubjectChange,
      onLoading: (isTranslating) => {
        dispatch(updateIsTranslatingAction({ languageCode: messageInputLanguage, isTranslating }));
      },
    });

  // reset refs to subject/messageBody when translation is hidden
  useEffect(() => {
    if (!shouldShowTranslation && originalSubject.current !== subject) {
      originalSubject.current = subject;
    }
  }, [shouldShowTranslation, subject]);
  useEffect(() => {
    if (!shouldShowTranslation && originalMessageBody.current !== messageBody) {
      originalMessageBody.current = messageBody;
    }
  }, [shouldShowTranslation, messageBody]);

  // the following useEffect sets shouldShowTranslation to false and clears out
  // the originalMessageBody when a message has just been sent, which we
  // qualify by checking if the MessageInput has disabled the button
  useEffect(() => {
    if (isMessageSending) {
      reset();
      originalMessageBody.current = '';
      originalSubject.current = '';
    }
  }, [isMessageSending, reset]);

  const isTranslateDisabled = isMessageSending || !messageBody || loading || disabled || false;

  const handleClick = () => {
    if (shouldShowTranslation) {
      // Revert the translation
      hideTranslation();
      dispatch(
        updateMessageForLanguageAction(
          {
            languageCode: messageInputLanguage,
            message: originalMessageBody.current,
          },
          {
            // force consumers maintaining their own state to reflect this update
            forceUpdate: true,
          },
        ),
      );
      if (onSubjectChange && originalSubject.current) {
        onSubjectChange(originalSubject.current);
      }
    } else if (languageSelection) {
      onTranslateMessageClick({ messageBody, languageCode: languageSelection, subject });
    } else {
      handleOpenDialogClick();
    }
  };

  const handleOpenDialogClick = () => {
    setDialogVisible(true);
  };

  const handleCloseDialogClick = () => {
    setDialogVisible(false);
    const newLanguageSelection = getCachedSelectedTranslationLanguage();
    if (newLanguageSelection && newLanguageSelection !== languageSelection) {
      if (onSubjectChange && originalSubject.current) {
        onSubjectChange('');
      }
      // Use the original message and subject when translating from
      // a language to a new language to avoid translating already translated
      // text and have the meaning of the message stray from the original
      // intention
      onTranslateMessageClick({
        messageBody: originalMessageBody.current,
        subject: originalSubject.current,
        languageCode: newLanguageSelection,
      });
    }
  };

  // the button should read "Translate into ..." if either:
  // a message was just sent,
  // shouldShowTranslation is true,
  // or a translation is streaming in
  const shouldRenderTranslateText = Boolean(isMessageSending || !shouldShowTranslation || loading);
  const renderTranslateButton = (splitButtonProps: SplitButtonRenderProps) => (
    <TranslateActionButton
      shouldRenderTranslateText={shouldRenderTranslateText}
      shouldRenderSplitButton={shouldRenderSplitButton}
      handleClick={handleClick}
      loading={loading}
      isLanguageSpecificButton={isLanguageSpecificButton}
      languageName={languageName}
      {...splitButtonProps}
    />
  );

  const renderLanguageButtons = (splitButtonProps: SplitButtonRenderProps) => (
    <ChangeLanguageButton handleOpenDialogClick={handleOpenDialogClick} {...splitButtonProps} />
  );

  return (
    <>
      <Tooltip title={disabledTooltip || ''} placement="top">
        <span>
          <div data-cy="translate-split-button">
            {shouldRenderSplitButton ? (
              <UiSplitButton
                buttonGroupClassName="u-boxShadow-none"
                disabled={isTranslateDisabled}
                MainButton={renderTranslateButton}
                DropdownButtons={renderLanguageButtons}
                popperButtonVariant="tertiary"
                popperButtonSize="xxs"
                popperClassName="u-position-top-sticky u-lineHeight-normal"
                dropdownFontSize="xs"
              />
            ) : (
              <TranslateActionButton
                disabled={isTranslateDisabled}
                shouldRenderTranslateText={shouldRenderTranslateText}
                shouldRenderSplitButton={shouldRenderSplitButton}
                handleClick={handleClick}
                loading={loading}
                isLanguageSpecificButton={isLanguageSpecificButton}
                languageName={languageName}
              />
            )}
          </div>{' '}
        </span>
      </Tooltip>
      {dialogVisible && (
        <TranslationLanguageSelectorDialog closeDialogClickCallback={handleCloseDialogClick} />
      )}
    </>
  );
};
